import { ReactNode } from 'react';

import { tokens, Typography, Box, Stack } from '@cast/design-system';

type AgentTimelineProps = {
  label: string;
  steps: Array<{
    title: string;
    description: string;
    icon: ReactNode;
  }>;
};

const SIDE_PANEL_WIDTH = 360;

export const AgentTimeline = ({ label, steps }: AgentTimelineProps) => (
  <Stack
    flex={`0 0 ${SIDE_PANEL_WIDTH}px`}
    width={SIDE_PANEL_WIDTH}
    maxWidth={SIDE_PANEL_WIDTH}
    gap={12}
    p={24}
    alignSelf="stretch"
    sx={{
      bgcolor: tokens.theme.bg.structural.surfaceRaised,
      borderLeft: '1px solid',
      borderColor: tokens.theme.border.structural.neutralSubtle,
      minHeight: 0,
      boxSizing: 'border-box',
    }}
  >
    <Typography
      variant="L12B"
      sx={{
        color: tokens.theme.fg.structural.secondary,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>

    <Stack flex={1} minHeight={0} gap={0}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;

        return (
          <Stack
            key={step.title}
            direction="row"
            gap={8}
            flex={isLast ? undefined : 1}
            minHeight={0}
            alignItems="stretch"
          >
            <Stack
              alignItems="center"
              sx={{
                width: 28,
                flexShrink: 0,
                py: '2px',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '16px',
                  bgcolor: tokens.theme.bg.structural.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: tokens.theme.fg.action.primary,
                  flexShrink: 0,
                  p: '4px',
                }}
              >
                {step.icon}
              </Box>
              {!isLast ? (
                <Box
                  aria-hidden
                  sx={{
                    flex: 1,
                    width: '1px',
                    minHeight: 12,
                    bgcolor: tokens.theme.border.structural.neutral,
                  }}
                />
              ) : null}
            </Stack>

            <Stack gap={0} minWidth={0} flex={1} pb={isLast ? 0 : 16}>
              <Typography
                variant="A1"
                sx={{ color: tokens.theme.fg.structural.primary }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="P12R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                {step.description}
              </Typography>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  </Stack>
);
